// Import the Firebase Functions client SDK
// Make sure firebase-functions.js is imported in index.html
import { getFunctions, httpsCallable } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-functions.js";

// References to callable functions (initialized later)
let getPlayerStatsFunction;
let getPlayerProgressionFunction;

/**
 * Fetches player stats via a Cloud Function.
 * @param {string} userId - The user's unique ID.
 * @returns {Promise<object>} A promise that resolves with the stats object { gamesPlayed, wins, losses } or rejects with an error.
 */
export async function getPlayerStats(userId) {
  if (!userId) {
     // Return a rejected promise or throw an error if no userId is provided
     return Promise.reject(new Error("User ID is required to fetch player stats."));
  }

  // Initialize the Cloud Function reference if not already done
  if (!getPlayerStatsFunction) {
      try {
          const functions = getFunctions(); // Get default Functions instance
          getPlayerStatsFunction = httpsCallable(functions, 'getPlayerStatsCallable');
      } catch (error) {
          console.error("getPlayerStats: Error getting Firebase Functions instance:", error);
          // Return a rejected promise
          return Promise.reject(new Error("Error initializing stats service."));
      }
  }

  try {
    // Call the Cloud Function (no arguments needed as context provides userId)
    console.log("getPlayerStats: Calling getPlayerStatsCallable Cloud Function...");
    const result = await getPlayerStatsFunction();
    const stats = result.data; // The stats object is in result.data
    console.log("getPlayerStats: Received stats from Cloud Function:", stats);

    if (!stats) {
        // Throw an error if no stats data is received
        throw new Error("No stats data received from function call.");
    }

    // Return the fetched stats data
    return stats;

  } catch (error) {
    console.error("getPlayerStats: Error calling Cloud Function or processing stats:", error);
    let errorMessage = "Could not load stats at this time.";
    if (error.code === 'unauthenticated') {
        errorMessage = "Authentication error retrieving stats.";
    } else if (error.message) {
        errorMessage = error.message; // Use the specific error message
    }
    // Throw an error or return a rejected promise
    throw new Error(errorMessage);
  }
}

/**
 * Fetches player progression data via a Cloud Function.
 * @returns {Promise<object>} A promise that resolves with the progression object.
 */
export async function getPlayerProgression() {
  if (!getPlayerProgressionFunction) {
    try {
      const functions = getFunctions();
      getPlayerProgressionFunction = httpsCallable(functions, 'getPlayerProgressionCallable');
    } catch (error) {
      console.error("getPlayerProgression: Error getting Firebase Functions instance:", error);
      return Promise.reject(new Error("Error initializing progression service."));
    }
  }

  try {
    console.log("getPlayerProgression: Calling getPlayerProgressionCallable Cloud Function...");
    const result = await getPlayerProgressionFunction();
    const progression = result.data;
    console.log("getPlayerProgression: Received progression from Cloud Function:", progression);

    if (!progression) {
      throw new Error("No progression data received from function call.");
    }

    return progression;
  } catch (error) {
    console.error("getPlayerProgression: Error calling Cloud Function:", error);
    let errorMessage = "Could not load progression at this time.";
    if (error.code === 'unauthenticated') {
      errorMessage = "Authentication error retrieving progression.";
    } else if (error.message) {
      errorMessage = error.message;
    }
    throw new Error(errorMessage);
  }
}